import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Agent proposal type for human-in-the-loop features
interface AgentProposal {
  id: string;
  type: 'annotation' | 'calculation' | 'suggestion';
  content: any;
  reasoning: string;
  status: 'pending' | 'accepted' | 'rejected';
  timestamp: Date;
}

interface BlueprintContextType {
  // Current state
  currentPage: number;
  totalPages: number;
  annotations: any[];
  selectedAnnotation: any;
  
  // Agent state
  agentProposals: AgentProposal[];
  agentMemory: Map<string, any>;
  
  // PDF data
  pdfFile: File | null;
  pdfBase64: string | null;
  legendData: any;
  
  // Canvas reference
  pageSize: { width: number; height: number };
  
  // Actions
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  addAnnotation: (annotation: any) => void;
  removeAnnotation: (id: string) => void;
  setAnnotations: (annotations: any[]) => void;
  acceptProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string, reason?: string) => void;
  setPdfFile: (file: File | null) => void;
  setPdfBase64: (base64: string | null) => void;
  setPageSize: (size: { width: number; height: number }) => void;
  
}

const BlueprintContext = createContext<BlueprintContextType | null>(null);

export function BlueprintProvider({ children }: { children: React.ReactNode }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [annotations, setAnnotations] = useState<any[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<any>(null);
  const [agentProposals, setAgentProposals] = useState<AgentProposal[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [legendData, setLegendData] = useState<any>(null);
  const [pageSize, setPageSize] = useState({ width: 816, height: 1056 });
  
  const agentMemoryRef = useRef(new Map<string, any>());
  
  // Add annotation
  const addAnnotation = useCallback((annotation: any) => {
    setAnnotations(prev => [...prev, annotation]);
  }, []);
  
  // Remove annotation
  const removeAnnotation = useCallback((id: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== id));
  }, []);
  
  // Accept agent proposal
  const acceptProposal = useCallback(async (proposalId: string) => {
    const proposal = agentProposals.find(p => p.id === proposalId);
    if (proposal && proposal.type === 'annotation') {
      // Add the proposed annotation
      addAnnotation(proposal.content);
    }
    
    // Memory updates can be tracked locally if needed
    agentMemoryRef.current.set(`accepted_${proposal?.id}`, {
      type: proposal?.type,
      characteristics: proposal?.content
    });
    
    setAgentProposals(prev => 
      prev.map(p => p.id === proposalId ? { ...p, status: 'accepted' as const } : p)
    );
  }, [agentProposals, addAnnotation]);
  
  // Reject agent proposal
  const rejectProposal = useCallback(async (proposalId: string, reason?: string) => {
    const proposal = agentProposals.find(p => p.id === proposalId);
    
    // Memory updates can be tracked locally if needed
    if (proposal) {
      agentMemoryRef.current.set(`rejected_${proposal.id}`, {
        type: proposal.type,
        reason
      });
    }
    
    setAgentProposals(prev => 
      prev.map(p => p.id === proposalId ? { ...p, status: 'rejected' as const } : p)
    );
  }, [agentProposals]);
  
  const value: BlueprintContextType = {
    currentPage,
    totalPages,
    annotations,
    selectedAnnotation,
    agentProposals,
    agentMemory: agentMemoryRef.current,
    pdfFile,
    pdfBase64,
    legendData,
    pageSize,
    setCurrentPage,
    setTotalPages,
    addAnnotation,
    removeAnnotation,
    setAnnotations,
    acceptProposal,
    rejectProposal,
    setPdfFile,
    setPdfBase64,
    setPageSize,
  };
  
  return (
    <BlueprintContext.Provider value={value}>
      {children}
    </BlueprintContext.Provider>
  );
}

export const useBlueprintContext = () => {
  const context = useContext(BlueprintContext);
  if (!context) {
    throw new Error('useBlueprintContext must be used within BlueprintProvider');
  }
  return context;
};
